import React, { useEffect, useState } from "react";
import { Icon } from "semantic-ui-react";

import { SHOW_CHATLOG_EVENT } from "../features/evaluation_sessions/evaluationRules";
import { JUMP_STACK_EVENT, jumpBack, jumpDepth, jumpTo } from "../features/evaluation_sessions/reportJump";

/**
 * The pieces a report card is made of, shared by the 응대 스킬 criteria cards
 * (EvaluationCard) and the 제품 지식 claim cards (ProductKnowledgeCriteriaList) so the two
 * read as one design. Site style, not Semantic's (docs/FRONTEND_UI.md).
 */

/**
 * A section header between groups of cards: 응대 스킬 요약 / 평가 항목, 평가 요약 / 추출된 주장,
 * the dashboard's 판매 스킬 / AI 코칭 인사이트 / … One step above the cards' own headings
 * (영역별 분석 is 15px/600). `meta` is a grey count beside it, `sub` a grey line below it.
 * `first` drops the top space when it opens a tab or a page.
 */
export const SectionTitle = ({ children, meta, sub, first }) => (
  <div style={{ margin: first ? "6px 2px 10px" : "22px 2px 10px" }}>
    <div style={{ display: "flex", alignItems: "baseline", gap: "8px" }}>
      <span style={{ fontSize: "16px", fontWeight: 700, color: "#1d1d1f" }}>{children}</span>
      {meta && <span style={{ fontSize: "13px", color: "#86868b" }}>{meta}</span>}
    </div>
    {sub && <div style={{ fontSize: "13px", color: "#86868b", marginTop: "2px" }}>{sub}</div>}
  </div>
);

/** The white card every trainee page builds on (docs/FRONTEND_UI.md). */
export const cardStyle = {
  backgroundColor: "white",
  borderRadius: "16px",
  border: "1px solid rgba(0,0,0,0.05)",
  boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
  padding: "16px",
};

/** Score colours: green from 80, orange from 50, red below. */
export const scoreColor = (score) => (score >= 80 ? "#34C759" : score >= 50 ? "#FF9500" : "#FF3B30");

/**
 * One number with its label and an optional grey line under it: the report's 응대 스킬 /
 * 제품 지식 tiles and the dashboard's stats. `color` defaults to the text colour; `tint` gives
 * the tile a background (복습 필요 when something is due).
 */
export const StatTile = ({ label, value, color = "#1d1d1f", sub, tint }) => (
  <div style={{ ...cardStyle, padding: "14px", textAlign: "center", backgroundColor: tint || "white", display: "flex", flexDirection: "column", justifyContent: "center" }}>
    <div style={{ fontSize: "12px", fontWeight: 600, color: "#86868b" }}>{label}</div>
    <div style={{ fontSize: "24px", fontWeight: 700, lineHeight: 1.3, color }}>{value}</div>
    {sub && <div style={{ fontSize: "11px", color: "#86868b", lineHeight: 1.4 }}>{sub}</div>}
  </div>
);

/** A soft status pill: green when the outcome is good for the trainee, red when not. */
export const StatusPill = ({ favorable, children, title }) => (
  <span
    title={title}
    style={{
      display: "inline-block",
      flexShrink: 0,
      padding: "3px 10px",
      borderRadius: "999px",
      fontSize: "12px",
      fontWeight: 700,
      whiteSpace: "nowrap",
      backgroundColor: favorable ? "#E8F8EE" : "#FDECEC",
      color: favorable ? "#1E8E3E" : "#D93025",
    }}
  >
    {children}
  </span>
);

/** A labelled block inside an opened card: 평가 근거, 관련 말풍선, 비교한 자료. */
export const DetailSection = ({ label, children }) => (
  <div style={{ marginTop: "14px" }}>
    <div style={{ fontSize: "12px", fontWeight: 600, color: "#86868b", marginBottom: "6px" }}>{label}</div>
    {children}
  </div>
);

export const greyBox = {
  backgroundColor: "#F5F5F7",
  borderRadius: "10px",
  padding: "10px 12px",
  fontSize: "13px",
  color: "#424245",
  lineHeight: 1.6,
  wordBreak: "keep-all",
};

/**
 * The trainee bubbles a result rests on: a blue "말풍선 N" pill and the text, tap to jump to it
 * in 대화 내용 (phones switch tab). `bubbles`: [{ id, chatlog_pk, text }]. `unit` is 문장 on
 * evaluations graded before #234's per-bubble numbering.
 */
export const BubbleList = ({ bubbles, unit = "말풍선" }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
    {bubbles.map((b) => (
      <button
        key={`${b.id}-${b.chatlog_pk}`}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          jumpTo(b.chatlog_pk, { showEvent: SHOW_CHATLOG_EVENT, detail: b.chatlog_pk, origin: e.currentTarget });
        }}
        style={{ ...greyBox, display: "flex", alignItems: "flex-start", gap: "8px", width: "100%", textAlign: "left", border: "none", cursor: "pointer", fontFamily: "inherit" }}
      >
        <span style={{ flexShrink: 0, backgroundColor: "#007AFF", color: "white", borderRadius: "999px", padding: "1px 8px", fontSize: "11px", fontWeight: 600, lineHeight: "20px", marginTop: "1px" }}>
          {unit} {b.id}
        </span>
        <span>{b.text}</span>
      </button>
    ))}
  </div>
);

/**
 * "← 돌아가기" after a jump in the report (reportJump.js): back to the tab, scroll and card
 * the reader came from. Same as the phone's back gesture; the count shows chained jumps.
 */
export const JumpBackPill = () => {
  const [depth, setDepth] = useState(jumpDepth());
  useEffect(() => {
    const update = () => setDepth(jumpDepth());
    window.addEventListener(JUMP_STACK_EVENT, update);
    return () => window.removeEventListener(JUMP_STACK_EVENT, update);
  }, []);
  if (!depth) return null;
  return (
    <button
      type="button"
      onClick={jumpBack}
      style={{
        position: "fixed",
        left: "50%",
        transform: "translateX(-50%)",
        bottom: "calc(24px + env(safe-area-inset-bottom))",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        gap: "6px",
        padding: "10px 18px",
        border: "none",
        borderRadius: "999px",
        backgroundColor: "#1d1d1f",
        color: "white",
        fontSize: "14px",
        fontWeight: 600,
        fontFamily: "inherit",
        boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
        cursor: "pointer",
      }}
    >
      <Icon name="arrow left" className="centered-icon" />
      돌아가기{depth > 1 ? ` (${depth})` : ""}
    </button>
  );
};

/**
 * The card itself: chevron, a number (`numberColor` carries meaning — orange is 평가 기준),
 * the title, optional small `meta` below it, a pill on the right; tapping opens `children`.
 */
export const ResultCard = ({ id, number, numberColor = "#86868b", title, meta, pill, busy, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      id={id}
      style={{
        position: "relative",
        backgroundColor: "white",
        borderRadius: "16px",
        border: "1px solid rgba(0,0,0,0.05)",
        boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
        overflow: "hidden",
      }}
    >
      {busy && (
        <div style={{ position: "absolute", inset: 0, backgroundColor: "rgba(255,255,255,0.7)", zIndex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon name="spinner" loading />
        </div>
      )}
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        style={{ display: "flex", alignItems: "flex-start", gap: "10px", width: "100%", padding: "14px 16px", border: "none", background: "none", cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}
      >
        <Icon name={open ? "chevron down" : "chevron right"} style={{ margin: "3px 0 0", color: "#c7c7cc", fontSize: "12px", flexShrink: 0 }} />
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: "block", fontSize: "14.5px", fontWeight: 600, lineHeight: 1.45, color: "#1d1d1f", wordBreak: "keep-all" }}>
            <span style={{ color: numberColor, fontWeight: 700, marginRight: "6px" }}>{number}.</span>
            {title}
          </span>
          {meta && <span style={{ display: "block", marginTop: "4px", fontSize: "12px", color: "#86868b" }}>{meta}</span>}
        </span>
        {pill}
      </button>
      {open && <div style={{ padding: "0 16px 16px", borderTop: "1px solid #f2f2f2" }}>{children}</div>}
    </div>
  );
};
